import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { subjectTheme } from "@/lib/subjects";
import { CourseUnits, parseLesson } from "@/components/course/CourseUnits";
import { AddItemDialog } from "@/components/course/AddItemDialog";

export { parseLesson };

function SubjectPanel({ c, editable, onOpen, onProgress }) {
  const [open, setOpen] = useState(false);
  const [course, setCourse] = useState(null);
  const [adding, setAdding] = useState(null);
  const t = subjectTheme(c.section, c.section_name);
  const load = useCallback(() => api.get(`/courses/${c.program}/${c.section}`).then((r) => setCourse(r.data)), [c.program, c.section]);
  useEffect(() => { if (open && !course) load(); }, [open, course, load]);
  const addUnit = async () => {
    const p = parseLesson(window.prompt("Lesson number and name, e.g. 2.1 Forces and motion"));
    if (!p.title) return;
    await api.post(`/courses/${c.program}/${c.section}/units`, p); toast.success("Lesson added."); load();
  };
  const shown = course || c;
  const pct = shown.hw_total ? Math.round((100 * shown.hw_done) / shown.hw_total) : 0;
  return (
    <div className="overflow-hidden rounded-2xl border border-[#EEEAF8] bg-white" data-testid={`subject-panel-${c.section}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`subject-toggle-${c.section}`} className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-[#FAF9FE]">
        <span className="h-10 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: t.hex }} />
        <span className="min-w-0 flex-1">
          <span className="block text-base font-extrabold text-[#1A1530]">{c.section_name}</span>
          <span className="block text-xs text-[#6B6488]">{c.lessons} lesson{c.lessons === 1 ? "" : "s"}{editable ? "" : ` · ${shown.hw_done}/${shown.hw_total} homework complete`}</span>
        </span>
        {!editable && <span className="hidden w-28 sm:block"><span className="block h-1.5 overflow-hidden rounded-full bg-[#F1EEFA]"><span className="block h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: t.hex }} /></span></span>}
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#948EAB] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#F1EEFA] bg-[#FCFBFF] p-3 sm:p-4">
          {course ? (
            <>
              {editable && <div className="mb-3 flex justify-end"><Button size="sm" onClick={addUnit} data-testid={`add-unit-${c.section}`} className="rounded-xl ember-gradient text-xs font-bold"><Plus className="mr-1 h-4 w-4" /> Add lesson</Button></div>}
              <CourseUnits course={course} editable={editable} onOpen={onOpen} onChanged={() => { load(); onProgress?.(); }} onAddItem={setAdding} />
              {editable && <AddItemDialog unit={adding} course={course} onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />}
            </>
          ) : <p className="p-4 text-sm text-[#948EAB]">Loading…</p>}
        </div>
      )}
    </div>
  );
}

export function ProgramCourse({ program, editable, onOpen, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [subjects, setSubjects] = useState(null);
  const load = useCallback(() => api.get("/courses", { params: program ? { program } : {} }).then((r) => setSubjects(r.data)), [program]);
  useEffect(() => { setSubjects(null); load(); }, [load]);
  if (!subjects) return null;
  const done = subjects.reduce((s, c) => s + c.hw_done, 0);
  const total = subjects.reduce((s, c) => s + c.hw_total, 0);
  const pct = total ? Math.round((100 * done) / total) : 0;
  const name = subjects[0]?.program_name || "Your course";
  return (
    <div data-testid={`program-course-${subjects[0]?.program}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="program-course-toggle"
        className="flex w-full items-center gap-4 rounded-2xl bg-[#4B2BB8] p-5 text-left text-white transition-colors hover:bg-[#3A1F96] sm:p-6">
        <span className="min-w-0 flex-1">
          <span className="block font-mono-t text-xs tracking-wider text-white/70">{subjects.length} subjects</span>
          <span className="mt-0.5 block text-2xl font-extrabold">{name}</span>
          {!editable && (
            <span className="mt-3 block max-w-md">
              <span className="flex justify-between text-xs font-semibold text-white/85" data-testid="program-course-progress"><span>{done}/{total} homework complete</span><span>{pct}%</span></span>
              <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-white/25"><span className="block h-full rounded-full bg-white" style={{ width: `${pct}%` }} /></span>
            </span>
          )}
        </span>
        <ChevronDown className={`h-6 w-6 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="program-course-subjects">
          {subjects.map((c) => <SubjectPanel key={c.section} c={c} editable={editable} onOpen={onOpen} onProgress={load} />)}
        </div>
      )}
    </div>
  );
}
